/**
 * Resolves the API base URL for the current account's region and environment.
 *
 * This is the single source of truth for "what host do I talk to?" —
 * used by `ts-rest.ts` for contract-based calls and `upload-asset.ts`
 * for multipart uploads. Previously each had its own inline URL map;
 * consolidating here prevents drift if hosts change.
 *
 * Reads region from `window.__hydra__` (set by the app shell before any
 * API call fires). Defaults to US when no account context is available
 * (e.g. login/auth flows).
 */
export function getApiBaseUrl(): string {
  const region = window.__hydra__?.account?.dc ?? 'us';

  if (__CIO_ENV__ === 'production') {
    return (
      {
        us: 'https://us.fly.customer.io',
        eu: 'https://eu.fly.customer.io',
      }[region] ?? 'https://us.fly.customer.io'
    );
  }

  if (__CIO_ENV__ === 'standalone') {
    return (
      {
        us: 'https://us.fly-standalone.customer.io',
        eu: 'https://us.fly-standalone.customer.io',
      }[region] ?? 'https://us.fly-standalone.customer.io'
    );
  }

  // Development — proxy through Vite / local dev server
  return '';
}

/**
 * The API base URL pinned to the US data center, regardless of the current
 * account's region. The SSO/SCIM configuration endpoints are served only from
 * the US data center — EU accounts that hit their own region time out
 * (opsbugs/4138) — so requests to them must always target the US host. In
 * development this resolves to the same empty string as {@link getApiBaseUrl}
 * (requests proxy through the dev server), so the pin is a no-op locally and in
 * tests.
 */
export function getUsApiBaseUrl(): string {
  if (__CIO_ENV__ === 'production') {
    return 'https://us.fly.customer.io';
  }

  if (__CIO_ENV__ === 'standalone') {
    return 'https://us.fly-standalone.customer.io';
  }

  return '';
}

/**
 * The base URL for the Design Studio Node.js server-api (`/design-studio/api`).
 *
 * Same as {@link getApiBaseUrl} in production (the region-prefixed hosts route
 * these paths fine — `https://us.fly.customer.io/design-studio/api/*` is what
 * production serves today), but standalone is **not** region-prefixed:
 * `https://us.fly-standalone.customer.io/design-studio/api/*` 404s, while
 * `https://fly-standalone.customer.io/design-studio/api/*` reaches server-api.
 * That matches parcel, the source of truth for these routes, where "staging"
 * (hydra's `standalone`) points both regions at the one un-prefixed host
 * (`parcel/app/vite.config.ts` → `env.apiUrl` / `env.euApiUrl`).
 *
 * Region comes from `window.__hydra__` like {@link getApiBaseUrl}, defaulting to
 * US. In development (and on devbox / prod-staging builds) this returns an empty
 * string so requests stay same-origin and proxy through the dev server
 * (`vite.config.ts` maps `/design-studio/api/` to local server-api).
 */
export function getDesignStudioApiBaseUrl(): string {
  if (__CIO_ENV__ === 'standalone') {
    return 'https://fly-standalone.customer.io';
  }

  return getApiBaseUrl();
}
